// stores/blockStore.js
import { get } from "svelte/store";
import { db } from "../db";
import logger from "../utils/logger";
import { blocks, currentDocument, showFlash } from "./documentStore";

// Update a block's content
export async function updateBlock(id, content) {
  try {
    logger.debug("Updating block", { blockId: id });
    await db.blocks.update(id, { content });

    // Update the local blocks array using store
    blocks.update((currentBlocks) =>
      currentBlocks.map((block) =>
        block.id === id ? { ...block, content } : block,
      ),
    );

    logger.debug("Block updated successfully", { blockId: id });
  } catch (error) {
    logger.error("Failed to update block", {
      error: error.message,
      blockId: id,
    });
    console.error("Error updating block:", error);
    showFlash("error", "Failed to update block");
  }
}

// Create a new block
export async function createBlock(index, focus = true) {
  try {
    const currentDoc = get(currentDocument);
    // Update positions in database first
    const currentBlocks = get(blocks);
    const blocksToUpdate = currentBlocks.filter(
      (block) => block.position > index,
    );

    // Update positions in database
    for (const block of blocksToUpdate) {
      await db.blocks.update(block.id, {
        position: block.position + 1,
      });
    }

    // Add the new block to database
    const newBlockId = await db.blocks.add({
      documentId: currentDoc.id,
      content: "", // Empty content for new block
      blockType: "text",
      position: index + 1,
      createdAt: new Date(),
    });

    // Now update the UI with the real ID
    blocks.update((currentBlocks) => [
      ...currentBlocks.slice(0, index + 1),
      {
        id: newBlockId,
        content: "",
        position: index + 1,
        blockType: "text",
        documentId: currentDoc.id,
        createdAt: new Date(),
      },
      ...currentBlocks.slice(index + 1).map((block) => ({
        ...block,
        position: block.position + 1,
      })),
    ]);

    logger.debug("New block created", {
      blockId: newBlockId,
      position: index + 1,
    });

    return { newBlockId, shouldFocus: focus === "new" };
  } catch (error) {
    logger.error("Failed to create block", {
      error: error.message,
      index,
    });
    console.error("Error creating block:", error);
    showFlash("error", "Failed to create block");
    return { newBlockId: null, shouldFocus: false };
  }
}

// Create a new block at the top position (position 0)
export async function createBlockAtTop(focus = false) {
  try {
    const currentDoc = get(currentDocument);
    logger.info("Creating new block at top position", {
      documentId: currentDoc.id,
    });

    // Update positions in database first
    const currentBlocks = get(blocks);
    // console.log("all blocks: ", currentBlocks);
    // All existing blocks need their positions incremented
    for (const block of currentBlocks) {
      await db.blocks.update(block.id, {
        position: block.position + 1,
      });
    }

    // Add the new block to database
    const newBlockId = await db.blocks.add({
      documentId: currentDoc.id,
      content: "", // Empty content for new block
      blockType: "text",
      position: 0,
      createdAt: new Date(),
    });

    // Now update the UI with the real ID
    blocks.update((currentBlocks) => [
      {
        id: newBlockId,
        content: "",
        position: 0,
        blockType: "text",
        documentId: currentDoc.id,
        createdAt: new Date(),
      },
      ...currentBlocks.map((block) => ({
        ...block,
        position: block.position + 1,
      })),
    ]);

    logger.debug("New block created at top position", {
      blockId: newBlockId,
    });

    return { newBlockId, shouldFocus: focus === "new" };
  } catch (error) {
    logger.error("Failed to create block at top", {
      error: error.message,
    });
    console.error("Error creating top block:", error);
    showFlash("error", "Failed to create block at top");
    return { newBlockId: null, shouldFocus: false };
  }
}

// Delete a block
export async function deleteBlock(id) {
  try {
    logger.info("Deleting block", { blockId: id });
    const currentBlocks = get(blocks);
    const blockToDelete = currentBlocks.find((block) => block.id === id);
    if (!blockToDelete) {
      logger.warn("Block not found for deletion", { blockId: id });
      return;
    }

    // Delete from database
    await db.blocks.delete(id);

    // Update positions for blocks after the deleted one
    const blocksToUpdate = currentBlocks.filter(
      (block) => block.position > blockToDelete.position,
    );

    for (const block of blocksToUpdate) {
      await db.blocks.update(block.id, {
        position: block.position - 1,
      });
    }

    // Update local blocks array
    blocks.update((currentBlocks) =>
      currentBlocks
        .filter((block) => block.id !== id)
        .map((block) =>
          block.position > blockToDelete.position
            ? { ...block, position: block.position - 1 }
            : block,
        ),
    );

    logger.debug("Block deleted successfully", { blockId: id });
  } catch (error) {
    logger.error("Failed to delete block", {
      error: error.message,
      blockId: id,
    });
    console.error("Error deleting block:", error);
    showFlash("error", "Failed to delete block");
  }
}

// Focus the first block in the document
export function getFirstBlockId() {
  const currentBlocks = get(blocks);
  if (currentBlocks.length > 0) {
    return currentBlocks[0].id;
  }
  return null;
}
